import React, { useState } from "react";
import { faCaretUp, faCaretDown, faTimes, faFilter, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover, OverlayTrigger } from "react-bootstrap";
import DatatableRow from "./DatatableRow";
import PropTypes from "prop-types";

const returnDragClass = (draggingKey, dragOverKey, col) => {
  let dragClass = "";
  if (draggingKey === col.key) {
    dragClass = "dragging";
  } else if (dragOverKey === col.key && draggingKey) {
    dragClass = "drag-target";
  }
  return dragClass;
};

const retrnBg = obj => obj ? 'inherit' : undefined
const retrnMin = obj => obj || "auto";
const retrnCurrentWidth = (obj, colAncho) => obj || colAncho;
const retrnSortdir = (obj, sortDir) => obj ? sortDir : null;

const DatatableTable = ({
  tableContainerRef,
  cols,
  colWidths,
  setColWidths,
  rows,
  loadingState,
  effectivePerPage,
  checkboxColumn,
  searchColumnsEnabled,
  nameColumnsUppercase,
  adjustColumnSize,
  rowIdField,
  onRowClick,
  onRowDoubleClick,
  draggingKey,
  dragOverKey,
  dragOverColIdx,
  selectedRows,
  toggleSelectAll,
  toggleSelectRow,
  sortBy,
  sortDir,
  handleSortClick,
  columnFilters,
  handleColumnFilterChange,
  inputHoverColIdx,
  setInputHoverColIdx,
  orderColumns,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragDropReset,
}) => {
  const [activeFilterColKey, setActiveFilterColKey] = useState(null);
  const visibleCols = cols.map((col, idx) => ({ ...col, originalIdx: idx })).filter(col => col.isVisible !== false);
  const perPage = effectivePerPage || 10;

  const handleResizerMouseMove = (columnIndex, startX, startWidth, ev) => {
    requestAnimationFrame(() => {
      const newWidth = Math.max(50, startWidth + (ev.clientX - startX));
      setColWidths(prev => {
        const updated = [...prev];
        updated[columnIndex] = newWidth + "px";
        return updated;
      });
    });
  };

  const handleResizerMouseDown = (e, columnIndex) => {
    if (!adjustColumnSize) return;
    e.preventDefault();
    e.stopPropagation();
    const headerCell = e.target.closest("th");
    if (!headerCell) return;
    const startX = e.clientX;
    const startWidth = headerCell.getBoundingClientRect().width;
    const onMouseMove = ev => handleResizerMouseMove(columnIndex, startX, startWidth, ev);
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div ref={tableContainerRef} className="datatable-table-container table-responsive border rounded shadow-sm datatable-scrollbar" style={{ position: "relative" }}>
      <table
        className="table table-sm table-hover mb-0 datatable-table"
        style={{
          tableLayout: adjustColumnSize ? "fixed" : "auto",
          width: adjustColumnSize ? "max-content" : "100%",
          minWidth: "100%",
        }}
      >
        <thead className="sticky-top" style={{ top: 0, zIndex: 5 }}>
          {/* Fila de nombres de columnas */}
          <tr className="datatable-th-container">
            {checkboxColumn && (
              <th rowSpan={searchColumnsEnabled ? 2 : 1} className="sticky-top datatable-th-checkbox">
                <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                  <input type="checkbox" onChange={toggleSelectAll} checked={rows.length > 0 && selectedRows.size === rows.length} style={{ cursor: "pointer", width: 16, height: 16 }} />
                </div>
              </th>
            )}
            {visibleCols.map((col, loopIdx) => {
              const isSorted = sortBy === col.key;
              const dir = retrnSortdir(isSorted, sortDir);
              const headerAlign = col.headerAlign || "left";
              const label = nameColumnsUppercase ? String(col.label).toUpperCase() : col.label;
              const colAncho = retrnMin(col.minWidth);
              const currentWidth = retrnCurrentWidth(colWidths[col.originalIdx], colAncho);

              let dragClass = returnDragClass(draggingKey, dragOverKey, col);

              const thStyle = {
                textAlign: headerAlign,
                width: currentWidth,
                minWidth: col.minWidth || (adjustColumnSize ? "50px" : "auto"),
                position: col.stickyRight ? "sticky" : "relative",
                right: col.stickyRight ? 0 : undefined,
                zIndex: col.stickyRight ? 5 : undefined,
                boxShadow: col.stickyRight ? "-5px 0 8px -3px rgba(0,0,0,0.08)" : undefined,
                borderRight: loopIdx < visibleCols.length - 1 ? "1px solid #dee2e6" : undefined,
                boxSizing: "border-box",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                transition: "background 0.2s, box-shadow 0.2s, border 0.2s",
              };

              return (
                <th
                  key={col.key}
                  scope="col"
                  style={thStyle}
                  className={`datatable-table-th ${dragClass}`}
                  draggable={orderColumns && !col.stickyRight}
                  onDragStart={e => {
                    if (e.target.closest(".th-resize-handle") || e.target.closest(".dropdown-menu") || e.target.closest(".dropdown-toggle") || e.target.tagName === "INPUT") {
                      e.preventDefault();
                      return false;
                    }
                    handleDragStart(e, col.key);
                  }}
                  onDragOver={e => handleDragOver(e, col.key, col.originalIdx)}
                  onDragLeave={e => handleDragLeave(e, col.key)}
                  onDrop={e => handleDrop(e, col.key)}
                  onDragEnd={() => {
                    handleDragDropReset?.();
                  }}
                >
                  <div className="d-flex align-items-center gap-1 justify-content-between">
                    <button
                      className="bg-transparent border-0 text-truncate flex-grow-1 text-start p-0"
                      style={{ userSelect: "none", cursor: col.sortable ? "pointer" : "default", fontWeight: 600, fontSize: "14px", color: "inherit" }}
                      title={col.sortable ? "Clic para ordenar" : label}
                      onClick={() => {
                        if (col.sortable) {
                          if (!isSorted) handleSortClick(col.key, "asc");
                          else if (dir === "asc") handleSortClick(col.key, "desc");
                          else handleSortClick("", "");
                        }
                      }}
                    >
                      {label}
                    </button>
                    <div className="d-flex align-items-center gap-1 text-nowrap">
                      {col.sortable && (
                        <div className="th-sort d-flex align-items-center">
                          <div style={{ position: "relative", width: "12px", height: "18px" }}>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                handleSortClick(col.key, isSorted && dir === "asc" ? "" : "asc");
                              }}
                              className={isSorted && dir === "asc" ? "datatable-th-sort-btn-active" : "datatable-th-sort-btn-inactive"}
                              style={{ position: "absolute", top: "0px", left: 0, padding: 0, border: "none", background: "transparent", width: "12px", height: "9px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}
                              title="Ordenar ascendente"
                            >
                              <FontAwesomeIcon icon={faCaretUp} style={{ fontSize: "14px", marginTop: "3px" }} />
                            </button>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                handleSortClick(col.key, isSorted && dir === "desc" ? "" : "desc");
                              }}
                              className={isSorted && dir === "desc" ? "datatable-th-sort-btn-active" : "datatable-th-sort-btn-inactive"}
                              style={{ position: "absolute", bottom: "0px", left: 0, padding: 0, border: "none", background: "transparent", width: "12px", height: "9px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}
                              title="Ordenar descendente"
                            >
                              <FontAwesomeIcon icon={faCaretDown} style={{ fontSize: "14px", marginBottom: "3px" }} />
                            </button>
                          </div>
                        </div>
                      )}
                      {col.searchable && searchColumnsEnabled && (
                        <div className="th-filter">
                          <OverlayTrigger
                            trigger="click"
                            rootClose
                            placement="bottom"
                            popperConfig={{ strategy: "fixed" }}
                            show={activeFilterColKey === col.key}
                            onToggle={(nextShow) => {
                              if (nextShow) {
                                setActiveFilterColKey(col.key);
                                return;
                              } 
                              if (activeFilterColKey === col.key) {
                                setActiveFilterColKey(null);
                              }
                            }}
                            overlay={
                              <Popover id={`filter-popover-${col.key}`} style={{ zIndex: 9999, padding: "10px", minWidth: "220px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #dee2e6" }}>
                                <Popover.Body className="p-1" onClick={e => e.stopPropagation()}>
                                  <div className="d-flex flex-column gap-2">
                                    <span className="small text-muted font-weight-bold" style={{ userSelect: "none" }}>Filtrar por {label}:</span>
                                    <div className="position-relative">
                                      <input
                                        type="text"
                                        value={columnFilters[col.key] || ""}
                                        onChange={e => handleColumnFilterChange(col.key, e.target.value)}
                                        className="form-control form-control-sm"
                                        placeholder="Escribe para buscar..."
                                        autoFocus
                                        style={{ paddingRight: columnFilters[col.key] ? "24px" : "8px" }}
                                      />
                                      {columnFilters[col.key] && (
                                        <button 
                                          type="button" 
                                          className="btn btn-sm position-absolute" 
                                          style={{ right: "4px", top: "50%", transform: "translateY(-50%)", padding: "0 4px", color: "#888", border: "none", background: "transparent" }} 
                                          onClick={() => handleColumnFilterChange(col.key, "")} 
                                          title="Limpiar filtro"
                                        >
                                          <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <button
                              type="button"
                              className="bg-transparent border-0 p-0 th-filter-btn"
                              style={{ color: columnFilters[col.key] ? '#0b2c4d' : 'rgba(11, 44, 77, 0.25)', cursor: 'pointer', outline: 'none' }}
                              title="Filtrar columna"
                              onClick={e => e.stopPropagation()}
                            >
                              <FontAwesomeIcon icon={faFilter} style={{ fontSize: "14px" }} />
                            </button>
                          </OverlayTrigger>
                        </div>
                      )}
                    </div>
                  </div>
                  {adjustColumnSize && <button className="th-resize-handle" onMouseDown={e => handleResizerMouseDown(e, col.originalIdx)} onClick={e => e.stopPropagation()} title="Arrastra para cambiar ancho" ></button>}
                </th>
              );
            })}
          </tr>
          {/* Fila de buscadores por columna eliminada en favor del filtro integrado en la cabecera */}
        </thead>
        <tbody>
          {loadingState ? (
            Array.from({ length: perPage }).map((_, rowIdx) => {
              const id = Number.parseInt(rowIdx);
              return (
                <tr key={id}>
                  {checkboxColumn && (
                    <td style={{ width: 40, verticalAlign: 'middle' }}>
                      <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                        <input type="checkbox" disabled style={{ width: 16, height: 16 }} />
                      </div>
                    </td>
                  )}
                  {visibleCols.map((col, colIdx) => {
                    const dataAlign = col.dataAlign || "left";
                    const isDragging = draggingKey === col.key;

                    const cellStyle = {
                      textAlign: dataAlign,
                      width: colWidths[col.originalIdx] || undefined,
                      maxWidth: colWidths[col.originalIdx] || undefined,
                      minWidth: col.minWidth || undefined,
                      borderRight: colIdx < visibleCols.length - 1 ? "1px solid #14406e22" : undefined,
                      color: "#222",
                      fontSize: 13,
                      padding: "8px 8px",
                      boxSizing: "border-box",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      transition: "background 0.2s, box-shadow 0.2s, border 0.2s",
                      position: col.stickyRight ? "sticky" : undefined,
                      right: col.stickyRight ? 0 : undefined,
                      zIndex: col.stickyRight ? 1 : undefined,
                      boxShadow: col.stickyRight ? "-5px 0 8px -3px rgba(0,0,0,0.08)" : undefined,
                      opacity: isDragging ? 0.4 : 1,
                      background: isDragging ? "rgba(42, 119, 185, 0.05)" : retrnBg(col.stickyRight),
                    };
                    return (
                      <td key={`skeleton-cell-${col.key}-${rowIdx}`} style={cellStyle}>
                        <div className="skeleton-box wave" style={{ height: 18, width: "100%", borderRadius: 6, margin: 0 }} />
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <>
              {rows.length === 0 ? (
                <>
                  {Array.from({ length: perPage }).map((_, idx) => {
                    const dummyIdx = 1 + idx;
                    const rowClassName = dummyIdx % 2 === 0 ? 'datatable-row-even' : 'datatable-row-odd';
                    return (
                      <tr key={`dummy-empty-row-${Number.parseInt(idx)}`} className={rowClassName} style={{ height: "39px" }}>
                        {checkboxColumn && <td className="datatable-td-checkbox" style={{ borderRight: "1px solid #14406e22" }} />}
                        {visibleCols.map((col, colIdx) => (
                          <td
                            key={`dummy-empty-cell-${col.key}-${idx}`}
                            className="datatable-td-cell"
                            style={{
                              width: colWidths[col.originalIdx] || undefined,
                              maxWidth: colWidths[col.originalIdx] || undefined,
                              minWidth: col.minWidth || undefined,
                              borderRight: colIdx < visibleCols.length - 1 ? "1px solid #14406e22" : undefined,
                              position: col.stickyRight ? "sticky" : undefined,
                              right: col.stickyRight ? 0 : undefined,
                              zIndex: col.stickyRight ? 1 : undefined,
                              boxShadow: col.stickyRight ? "-5px 0 8px -3px rgba(0,0,0,0.08)" : undefined,
                              background: col.stickyRight ? 'inherit' : undefined
                            }}
                          >
                            &nbsp;
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </>
              ) : (
                <>
                  {rows
                    .slice(0, perPage)
                    .map((row, idx) => (
                      <DatatableRow
                        key={row[rowIdField] ?? `${rowIdField}-${idx}`}
                        row={row}
                        idx={idx}
                        cols={visibleCols}
                        colWidths={colWidths}
                        checkboxColumn={checkboxColumn}
                        adjustColumnSize={adjustColumnSize}
                        rowIdField={rowIdField}
                        onRowClick={onRowClick}
                        onRowDoubleClick={onRowDoubleClick}
                        draggingKey={draggingKey}
                        dragOverColIdx={dragOverColIdx}
                        selectedRows={selectedRows}
                        toggleSelectRow={toggleSelectRow}
                      />
                    ))}
                  {Array.from({ length: Math.max(0, perPage - rows.length) }).map((_, idx) => {
                    const dummyIdx = rows.length + idx;
                    const rowClassName = dummyIdx % 2 === 0 ? 'datatable-row-even' : 'datatable-row-odd';
                    return (
                      <tr key={`dummy-row-${Number.parseInt(idx)}`} className={rowClassName} style={{ height: "39px" }}>
                        {checkboxColumn && <td className="datatable-td-checkbox" style={{ borderRight: "1px solid #14406e22" }} />}
                        {visibleCols.map((col, colIdx) => {
                          const isDragging = draggingKey === col.key;
                          return (
                            <td
                              key={`dummy-cell-${col.key}-${idx}`}
                              className="datatable-td-cell"
                              style={{
                                width: colWidths[col.originalIdx] || undefined,
                                maxWidth: colWidths[col.originalIdx] || undefined,
                                minWidth: col.minWidth || undefined,
                                borderRight: colIdx < visibleCols.length - 1 ? "1px solid #14406e22" : undefined,
                                position: col.stickyRight ? "sticky" : undefined,
                                right: col.stickyRight ? 0 : undefined,
                                zIndex: col.stickyRight ? 1 : undefined,
                                boxShadow: col.stickyRight ? "-5px 0 8px -3px rgba(0,0,0,0.08)" : undefined,
                                opacity: isDragging ? 0.4 : 1,
                                background: isDragging ? 'rgba(42, 119, 185, 0.05)' : retrnBg(col.stickyRight)
                              }}
                            >
                              &nbsp;
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </>
              )}
            </>
          )}
        </tbody>
      </table>
      {rows.length === 0 && !loadingState && (
        <div style={{ position: "absolute", top: "50px", left: 0, right: 0, bottom: 0, pointerEvents: "none", overflow: "hidden", zIndex: 10 }}>
          <div style={{ position: "sticky", left: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <FontAwesomeIcon icon={faFolderOpen} size="4x" className="mb-3" style={{ color: "#a5c2df" }} />
            <h5 className="mb-1" style={{ fontWeight: 600, color: "#14406e" }}>No se encontraron registros</h5>
          </div>
        </div>
      )}
    </div>
  );
};

DatatableTable.propTypes = {
  tableContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  cols: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      isVisible: PropTypes.bool,
      sortable: PropTypes.bool,
      searchable: PropTypes.bool,
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      headerAlign: PropTypes.string,
      dataAlign: PropTypes.string,
    }),
  ).isRequired,
  colWidths: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  setColWidths: PropTypes.func,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadingState: PropTypes.bool,
  effectivePerPage: PropTypes.number,
  checkboxColumn: PropTypes.bool,
  searchColumnsEnabled: PropTypes.bool,
  nameColumnsUppercase: PropTypes.bool,
  adjustColumnSize: PropTypes.bool,
  rowIdField: PropTypes.string.isRequired,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  draggingKey: PropTypes.string,
  dragOverKey: PropTypes.string,
  dragOverColIdx: PropTypes.number,
  selectedRows: PropTypes.instanceOf(Set),
  toggleSelectAll: PropTypes.func,
  toggleSelectRow: PropTypes.func,
  sortBy: PropTypes.string,
  sortDir: PropTypes.oneOf(["asc", "desc", ""]),
  handleSortClick: PropTypes.func,
  columnFilters: PropTypes.objectOf(PropTypes.string),
  handleColumnFilterChange: PropTypes.func,
  inputHoverColIdx: PropTypes.number,
  setInputHoverColIdx: PropTypes.func,
  orderColumns: PropTypes.bool,
  handleDragStart: PropTypes.func,
  handleDragOver: PropTypes.func,
  handleDragLeave: PropTypes.func,
  handleDrop: PropTypes.func,
  handleDragDropReset: PropTypes.func,
};

export default DatatableTable;
